// Firing the cash drawer.
//
// A drawer is a solenoid plugged into a printer's DK port — it has no
// network connection of its own, so "opening the drawer" is sending a pulse
// to the printer. The pulse is its own payload, never bundled into a
// receipt: bundling would mean a tenant with printing switched off, or a
// receipt that failed, leaves the cashier holding cash at a shut till.
//
// The server decides whether a pulse is warranted at all (policy for a sale,
// a single-use manager approval for a no-sale open). This module only
// carries the answer to the printer.
import { drawerService } from '../services/api';
import { sendPayload } from './printTransport';
import { resolveAssignedPrinterId } from './PrintingUtils';
import { getStationConfig } from '../station/stationConfig';

/**
 * Which till's drawer to open.
 *
 * `has_drawer` is per-printer precisely because a two-till shop has a drawer
 * under each till. Sending no printer id makes the server fall back to "the
 * tenant's oldest drawer-bearing receipt printer" — so till 2's cash sale
 * would pop till 1's drawer and leave till 2 shut, i.e. an unattended open
 * cash drawer plus a cashier who cannot bank the note.
 *
 * The answer is the station's assigned RECEIPT printer — the same id, read
 * the same way, that the receipt for this sale is dialled on (see
 * PrintingUtils.resolveNetworkProtocol). Deliberately not a second
 * mechanism: if the receipt and the drawer ever resolved differently, the
 * paper and the cash would come out of two different tills.
 *
 * An unassigned or unreadable station resolves to `undefined`, which keeps
 * the server's existing fallback exactly as it behaves today. A station
 * config read is local storage, never a network call, so this cannot delay
 * the pulse.
 */
async function resolveDrawerPrinterId(explicitPrinterId) {
    if (explicitPrinterId) return explicitPrinterId;
    try {
        const stationConfig = await getStationConfig();
        return resolveAssignedPrinterId(stationConfig, 'receipt') || undefined;
    } catch (err) {
        return undefined;
    }
}

/**
 * Fire the drawer for a completed sale. Never throws and never rejects: the
 * sale is already done, and a hardware problem must not surface as a failed
 * transaction. A sale that does not qualify sends nothing at all.
 */
export async function kickDrawerForSale(transactionId, printerId) {
    if (!transactionId) return;
    try {
        const target = await resolveDrawerPrinterId(printerId);
        const payload = await drawerService.payloadForSale(transactionId, target);
        // 204 -> null: this sale does not open the drawer. Not a failure.
        if (!payload) return;
        await sendPayload(payload);
    } catch (err) {
        console.warn('Drawer kick failed for sale', transactionId, err);
    }
}

/**
 * Fire the drawer with no sale attached. Unlike the sale path this reports
 * its outcome, because a cashier pressed a button and deserves an answer.
 * Still never rejects — the caller renders a toast, not an error boundary.
 */
export async function kickDrawerManually({
    managerApprovalId, printerId, requestedByEmployeeId, salesDate,
} = {}) {
    let payload;
    try {
        payload = await drawerService.openManually({
            manager_approval_id: managerApprovalId,
            printer_id: await resolveDrawerPrinterId(printerId),
            requested_by_employee_id: requestedByEmployeeId,
            sales_date: salesDate,
        });
    } catch (err) {
        // 403 (approval refused/spent) and 404 (no drawer printer) are both
        // "we are not opening the drawer", which is all the cashier needs.
        return { success: false, reason: 'not_authorised' };
    }

    try {
        const result = await sendPayload(payload);
        return result?.success
            ? { success: true }
            : { success: false, reason: result?.reason || 'unreachable' };
    } catch (err) {
        return { success: false, reason: 'unreachable' };
    }
}
